/**
 * Billing periods are Ethiopian calendar months.
 *
 * The committee's books run on Ge'ez months, so the app bills on them too. A
 * period reads "EC2019-01", which is Meskerem 2019. The prefix is there because
 * "2019-01" on its own is a perfectly good Gregorian month, and a period that
 * can be misread as the wrong calendar is worth two characters to prevent.
 *
 * Pagumen is not billed. It is five or six days, and nobody charges a month's
 * dues for it, so a year has twelve periods and the days of Pagumen belong to
 * the Nehase period that precedes them.
 */

import { PAGUMEN, ethiopicMonthName, fromEthiopic, toEthiopic } from "./ethiopic.js";

export type Period = string;

/** Pagumen is not one of them. */
export const MONTHS_PER_YEAR = 12;

/** Every billing month is exactly this long, which is why no due date is ever clamped. */
export const DAYS_PER_MONTH = 30;

const PERIOD_PATTERN = /^EC(\d{4})-(0[1-9]|1[0-2])$/;

export class PeriodError extends Error {}

export function isPeriod(value: string): value is Period {
  return PERIOD_PATTERN.test(value);
}

export function assertPeriod(value: string): asserts value is Period {
  if (!isPeriod(value)) {
    throw new PeriodError(`Expected a period of the form ECYYYY-MM, received "${value}"`);
  }
}

export function makePeriod(year: number, month: number): Period {
  if (!Number.isInteger(year) || year < 1 || year > 9999) {
    throw new PeriodError(`Invalid Ethiopian year ${year}`);
  }
  if (month === PAGUMEN) {
    throw new PeriodError("Pagumen is not a billing month");
  }
  if (!Number.isInteger(month) || month < 1 || month > MONTHS_PER_YEAR) {
    throw new PeriodError(`Invalid Ethiopian month ${month}`);
  }
  return `EC${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
}

export function splitPeriod(period: Period): { year: number; month: number } {
  assertPeriod(period);
  return {
    year: Number(period.slice(2, 6)),
    month: Number(period.slice(7, 9)),
  };
}

/**
 * The period an instant falls in, read in Addis. The days of Pagumen fall in
 * the Nehase period, since they are not billed on their own.
 */
export function periodOf(instant: Date): Period {
  const { year, month } = toEthiopic(instant);
  return makePeriod(year, Math.min(month, MONTHS_PER_YEAR));
}

/** The instant a given day of a period begins in Addis, as UTC. */
export function eatInstant(period: Period, dayOfMonth: number, hour = 0, minute = 0): Date {
  const { year, month } = splitPeriod(period);
  assertDayOfMonth(dayOfMonth);
  return fromEthiopic({ year, month, day: dayOfMonth }, hour, minute);
}

function assertDayOfMonth(day: number): void {
  if (!Number.isInteger(day) || day < 1 || day > DAYS_PER_MONTH) {
    throw new PeriodError(`Day of month must be between 1 and ${DAYS_PER_MONTH}, received ${day}`);
  }
}

export function addPeriods(period: Period, count: number): Period {
  const { year, month } = splitPeriod(period);
  const zeroBased = year * MONTHS_PER_YEAR + (month - 1) + count;
  return makePeriod(Math.floor(zeroBased / MONTHS_PER_YEAR), (zeroBased % MONTHS_PER_YEAR) + 1);
}

export function comparePeriods(a: Period, b: Period): number {
  assertPeriod(a);
  assertPeriod(b);
  return a < b ? -1 : a > b ? 1 : 0;
}

/** How many whole months from a to b. Negative when b precedes a. */
export function periodsBetween(a: Period, b: Period): number {
  const from = splitPeriod(a);
  const to = splitPeriod(b);
  return (to.year - from.year) * MONTHS_PER_YEAR + (to.month - from.month);
}

/** Inclusive run of periods, used to bill several months in one payment. */
export function periodRange(start: Period, count: number): Period[] {
  if (!Number.isInteger(count) || count < 1) {
    throw new PeriodError(`count must be a positive integer, received ${count}`);
  }
  return Array.from({ length: count }, (_, index) => addPeriods(start, index));
}

/**
 * The due date of a period. Every billing month is thirty days, so a due day
 * the accountant picks lands in every month and never needs adjusting.
 */
export function dueDateOf(period: Period, dueDayOfMonth: number): Date {
  return eatInstant(period, dueDayOfMonth);
}

/** How a period is written for a resident to read. */
export function formatPeriod(period: Period, script: "am" | "latin" = "am"): string {
  const { year, month } = splitPeriod(period);
  return `${ethiopicMonthName(month, script)} ${year}`;
}
